/**
 * RiderSubscriptionsEditor · v2.6.7 batch I
 *
 * Lives under Admin Console → Administration → Payments. Lets admin/supervisor
 * edit every field of every rider-facing subscription plan: name, monthly
 * price, ride credits, outside-Weatherford discount %, and the description
 * shown on each plan card. Saved via PUT /api/admin/pricing.
 */
import { useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Save, RotateCw, Loader2 } from "lucide-react";

const SEED_PLANS = [
  { id: "basic",   name: "Basic",   price: 65.99,   credits: 1,  outside_discount_pct: 5,  description: "1 Weatherford ride / month · 5% off outside Weatherford" },
  { id: "economy", name: "Economy", price: 139.99,  credits: 3,  outside_discount_pct: 10, description: "3 Weatherford rides / month · 10% off outside Weatherford" },
  { id: "premier", name: "Premier", price: 640.99,  credits: 6,  outside_discount_pct: 20, description: "6 Weatherford rides / month · 20% off outside Weatherford" },
  { id: "elite",   name: "Elite",   price: 2000.00, credits: 12, outside_discount_pct: 30, description: "12 Weatherford rides / month · 30% off outside Weatherford" },
];

export default function RiderSubscriptionsEditor() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoading(true);
    adminApi.get("/admin/pricing").then(r => {
      const incoming = (r.data?.subscription_plans || []).map(p => ({...p}));
      setPlans(incoming.length > 0 ? incoming : SEED_PLANS.map(p => ({...p})));
    }).catch(e => toast.error(e?.response?.data?.detail || "Load failed"))
    .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const set = (id, key, value) => {
    setPlans(prev => prev.map(p => p.id === id ? {...p, [key]: value} : p));
  };

  const save = async () => {
    // Coerce types so backend never receives strings where numbers belong
    const payload = plans.map(p => ({
      id: String(p.id || "").toLowerCase().trim(),
      name: String(p.name || "").trim(),
      price: Math.max(0, Number(p.price) || 0),
      credits: Math.max(0, parseInt(p.credits, 10) || 0),
      outside_discount_pct: Math.max(0, Math.min(100, Number(p.outside_discount_pct) || 0)),
      description: String(p.description || "").trim(),
    }));
    if (payload.some(p => !p.id || !p.name)) { toast.error("Every plan needs an id and a name"); return; }
    setSaving(true);
    try {
      await adminApi.put("/admin/pricing", { subscription_plans: payload });
      toast.success("Subscription plans saved · live for riders immediately");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <Card className="bg-zinc-900 border-zinc-800 p-8 text-center text-zinc-500" data-testid="subs-editor-loading"><Loader2 className="h-4 w-4 mx-auto animate-spin"/></Card>;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-4" data-testid="rider-subs-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-bold flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-400"/>Rider Subscription Plans</h3>
          <p className="text-[11px] text-zinc-500">Edit every field a rider sees when they tap <span className="text-zinc-300">Subscriptions</span> in their dashboard. Changes go live immediately.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="subs-editor-reload" variant="outline" size="sm" onClick={reload} className="bg-zinc-950 border-zinc-700 h-8 text-xs"><RotateCw className="h-3 w-3 mr-1"/>Reload</Button>
          <Button data-testid="subs-editor-save" onClick={save} disabled={saving} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-8 text-xs"><Save className="h-3 w-3 mr-1"/>Save plans</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {plans.map(p => (
          <Card key={p.id} className="bg-zinc-950 border-zinc-800 p-3 space-y-2" data-testid={`subs-editor-card-${p.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold">{p.id}</span>
              <span className="text-[10px] text-zinc-600">id (locked)</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase text-zinc-500">Display name</Label>
                <Input data-testid={`subs-editor-name-${p.id}`} value={p.name || ""} onChange={e => set(p.id, "name", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 text-sm"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Price ($)</Label>
                <Input data-testid={`subs-editor-price-${p.id}`} type="number" step="0.01" min="0" value={p.price ?? 0} onChange={e => set(p.id, "price", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 text-sm meter-display"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Ride credits / mo</Label>
                <Input data-testid={`subs-editor-credits-${p.id}`} type="number" step="1" min="0" value={p.credits ?? 0} onChange={e => set(p.id, "credits", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 text-sm meter-display"/>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase text-zinc-500">Outside Weatherford discount (%)</Label>
                <Input data-testid={`subs-editor-disc-${p.id}`} type="number" step="1" min="0" max="100" value={p.outside_discount_pct ?? 0} onChange={e => set(p.id, "outside_discount_pct", e.target.value)} className="bg-zinc-900 border-zinc-700 h-8 mt-1 text-sm meter-display"/>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase text-zinc-500">Description (shown on rider plan card)</Label>
                <Input data-testid={`subs-editor-desc-${p.id}`} value={p.description || ""} onChange={e => set(p.id, "description", e.target.value)} placeholder="e.g. 1 Weatherford ride / month · 5% off outside" className="bg-zinc-900 border-zinc-700 h-8 mt-1 text-sm"/>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Card>
  );
}
